import React, { useEffect, useState } from 'react';
import { getKpiProfile, updateKpiProfile } from '../api';
import { KPI_SECTIONS, PERIOD_OPTIONS } from '../constants/kpiSections';
import SettingsNav from '../components/SettingsNav';
import './Settings.css';

const KpiCard = ({ section, sectionData, computed, onSave }) => {
  const [values, setValues] = useState(() => {
    const initial = {};
    section.fields.forEach((f) => { initial[f.name] = sectionData?.[f.name] ?? ''; });
    return initial;
  });
  const [period, setPeriod] = useState(sectionData?.period || section.period?.defaultValue || 'month');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const handleChange = (name) => (e) => {
    setSaved(false);
    setValues((v) => ({ ...v, [name]: e.target.value }));
  };

  const handleSave = async () => {
    const payload = {};
    for (const f of section.fields) {
      const raw = values[f.name];
      if (raw === '' || raw === null || raw === undefined) { payload[f.name] = null; continue; }
      const num = Number(raw);
      if (Number.isNaN(num)) { setError('Please enter numbers only.'); return; }
      payload[f.name] = num;
    }
    if (section.period) payload.period = period;
    setSaving(true);
    setError('');
    try {
      await onSave(section.key, payload);
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Could not save — try again.');
    } finally {
      setSaving(false);
    }
  };

  const resultValue = computed?.[section.resultKey];
  const hasResult = resultValue !== null && resultValue !== undefined;

  return (
    <section className="settings-section glass-card kpi-settings-card">
      <div className="kpi-settings-card__head">
        <div>
          <h3 className="settings-section__title">{section.label}</h3>
          <p className="kpi-settings-card__desc">{section.description}</p>
        </div>
        <span className={`kpi-settings-card__status kpi-settings-card__status--${sectionData?.status || 'not_started'}`}>
          {(sectionData?.status || 'not_started').replace('_', ' ')}
        </span>
      </div>

      {section.period && (
        <label className="kpi-settings-card__period">
          <span className="settings-field__label">These figures are reported</span>
          <select
            className="kpi-settings-card__period-select"
            value={period}
            onChange={(e) => { setSaved(false); setPeriod(e.target.value); }}
          >
            {PERIOD_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </label>
      )}

      <div className="kpi-settings-card__fields">
        {section.fields.map((f) => (
          <label key={f.name} className="settings-field">
            <span className="settings-field__label">{f.label}</span>
            <div className="settings-field__input-wrap">
              {f.prefix && <span className="settings-field__prefix">{f.prefix}</span>}
              <input
                type="number"
                inputMode="decimal"
                className="settings-field__input settings-field__input--mono settings-field__input--bare"
                value={values[f.name] ?? ''}
                onChange={handleChange(f.name)}
                placeholder="0"
              />
            </div>
          </label>
        ))}
      </div>

      {hasResult && (
        <div className="kpi-settings-card__result">
          <span className="kpi-settings-card__result-label">{section.resultLabel}</span>
          <span className="kpi-settings-card__result-value">{resultValue}{section.resultSuffix}</span>
        </div>
      )}

      {error && <p className="settings-error">{error}</p>}

      <div className="kpi-settings-card__actions">
        {saved && <span className="settings-saved">Saved.</span>}
        <button className="btn btn--sm btn--gold" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
};

/**
 * SettingsKpis — /settings/kpis
 *
 * The same 5 KPI's collected during onboarding (KpiGateModal), shown
 * here as editable fields so the user can update their numbers any time
 * without re-running the full onboarding flow.
 */
const SettingsKpis = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await getKpiProfile();
        setProfile(res.data.profile);
      } catch (err) {
        setError(err.message || 'Could not load your KPI profile.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSaveSection = async (sectionKey, payload) => {
    const res = await updateKpiProfile({ [sectionKey]: payload });
    setProfile(res.data.profile);
  };

  return (
    <main className="settings-page">
      <SettingsNav />

      <div className="settings-page__header">
        <div>
          <h1 className="settings-page__title">KPI's</h1>
          <p className="settings-page__sub">
            The 5 core numbers MarginPro uses to configure your recommendations
            {profile ? ` — ${profile.completionPct}% complete.` : '.'}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="settings-note">Loading…</p>
      ) : error ? (
        <p className="settings-error">{error}</p>
      ) : (
        <div className="kpi-settings-list">
          {KPI_SECTIONS.map((section) => (
            <KpiCard
              key={section.key}
              section={section}
              sectionData={profile?.[section.key]}
              computed={profile?.computed}
              onSave={handleSaveSection}
            />
          ))}
        </div>
      )}
    </main>
  );
};

export default SettingsKpis;
